import { Avatar, Button } from '@bs/ui-components'
import { Link } from 'react-router-dom'

import { companyLogoUrl } from '@/services/assets'

export interface TalentJobCardProps {
  title: string
  company: string
  /** Bare GCS filename or already-absolute URL — resolved here via `companyLogoUrl()` so no
   * caller has to remember to. Absent (a company with no logo on file) falls back to `Avatar`'s
   * own initials-from-name rendering, never a fabricated placeholder. */
  companyLogoUrl?: string
  /** Only ever set for a resolved `companies` row — same "no slug, no link" rule
   * job-search-page.tsx uses, so an unresolved company never renders a link to a page that
   * doesn't exist for it. */
  companySlug?: string
  location: string
  workMode: string
  posted: string
  tags: string[]
  saved?: boolean
  applied?: boolean
  onSave?: () => void
  onApply?: () => void
  /** Distinct from `onApply` — that button is `disabled` once `applied` is true, so a caller that
   * wants an already-applied card to still be actionable (the Applied tab's own list) provides
   * this instead of trying to reuse `onApply`, which would never fire. */
  onWithdraw?: () => void
  onDecline?: () => void
  onOpen?: () => void
}

export function TalentJobCard({
  title,
  company,
  companyLogoUrl: rawLogoUrl,
  companySlug,
  location,
  workMode,
  posted: _posted,
  tags: _tags,
  saved,
  applied,
  onSave,
  onApply,
  onWithdraw,
  onDecline,
  onOpen,
}: TalentJobCardProps) {
  const logoSrc = companyLogoUrl(rawLogoUrl)
  return (
    <article className="group mb-[30px] flex min-h-[150px] items-center rounded-[20px] bg-white p-5 shadow-[0_2px_4px_rgba(0,0,0,0.15)] sm:p-6">
      <div className="flex w-full gap-4 sm:gap-6">
        <Avatar {...(logoSrc ? { src: logoSrc } : {})} name={company || title} className="size-[50px] shrink-0 text-base sm:size-[100px] sm:text-2xl" />

        <div className="flex min-w-0 flex-1 flex-col sm:flex-row">
          <div className="min-w-0 flex-1">
            <button type="button" onClick={onOpen} className="text-left">
              <h2 className="text-lg font-medium leading-[1.3] text-[#404040] transition group-hover:text-accent sm:text-[22px]">
                {title}
              </h2>
            </button>
            <div className="mt-2 flex flex-col gap-1 text-sm text-[#404040] sm:flex-row sm:items-center sm:gap-3 sm:text-base">
              {companySlug ? (
                <Link to={`/company/${companySlug}`} onClick={(event) => event.stopPropagation()} className="text-primary hover:text-accent hover:underline">
                  {company}
                </Link>
              ) : (
                <span className="text-primary">{company}</span>
              )}
              <span className="hidden sm:inline">•</span>
              <span>{location}</span>
              <span className="hidden sm:inline">•</span>
              <span>{workMode}</span>
            </div>
          </div>

          <div className="mt-3 flex items-end gap-2 sm:mt-0 sm:self-end">
            {applied && onWithdraw ? (
              <Button size="sm" variant="outline" onClick={onWithdraw}>
                Withdraw
              </Button>
            ) : (
              <Button
                size="sm"
                variant={applied ? 'outline' : 'default'}
                onClick={onApply}
                disabled={applied}
              >
                {applied ? 'Applied' : 'Apply'}
              </Button>
            )}
            {!applied ? (
              <Button size="sm" variant="outline" onClick={onSave}>
                {saved ? 'Remove saved' : 'Save'}
              </Button>
            ) : null}
            {!applied && !saved && onDecline ? (
              <Button size="sm" variant="ghost" onClick={onDecline}>
                Decline
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  )
}
